import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, FormControl, IconButton, InputLabel, MenuItem,
  Select, TextField, Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { myApi } from '../../config/axiosConfig';
import BtnAcciones from '../../components/plantilla/tabla/BtnAcciones';
import CrearEditarFuelAmerica from './modales/CrearEditarFuelAmerica';

const money = (value) => `$${Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;

const decimal = (value, digits = 3) => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits
});

const formatDate = (value) => value
  ? new Date(value).toLocaleString('en-US', { hour12: false })
  : '';

const CargasFuelAmerica = () => {
  const gridRef = useRef();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [unit, setUnit] = useState('');
  const [type, setType] = useState('TODO');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const abrirModal = (row = null) => {
    setSelected(row);
    setModalOpen(true);
  };

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await myApi.post('/cargasFuel/getFuelAmerica', {
        unidad: unit.trim() || null,
        tipo: type,
        fi: startDate ? startDate.format('YYYY-MM-DD') : null,
        ff: endDate ? endDate.format('YYYY-MM-DD') : null
      });
      setRows(response.data.success ? response.data.data : []);
    } catch (error) {
      console.error(error);
      setRows([]);
      Swal.fire('Error', 'No fue posible consultar Fuel America.', 'error');
    } finally {
      setLoading(false);
    }
  }, [unit, type, startDate, endDate]);

  useEffect(() => { buscar(); }, []); // Carga inicial solamente

  const eliminar = useCallback(async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar esta carga?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: 'green',
      cancelButtonColor: 'red'
    });

    if (!result.isConfirmed) return;

    try {
      await myApi.delete(`/cargasFuel/deleteFuelAmerica/${id}`);
      setRows((current) => current.filter((row) => row.id !== id));
      Swal.fire('Listo', 'Carga eliminada.', 'success');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.error || 'No se pudo eliminar.', 'error');
    }
  }, []);

  const columns = useMemo(() => [
    {
      field: 'date', headerName: 'FECHA', minWidth: 180, pinned: 'left',
      valueFormatter: ({ value }) => formatDate(value)
    },
    { field: 'asset_type', headerName: 'TIPO DE UNIDAD', minWidth: 160 },
    { field: 'asset', headerName: 'UNIDAD', minWidth: 120, pinned: 'left' },
    { field: 'driver', headerName: 'OPERADOR', minWidth: 260 },
    {
      field: 'gallons', headerName: 'CANTIDAD GLLS', minWidth: 160,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }) => decimal(value)
    },
    {
      field: 'amount', headerName: 'IMPORTE', minWidth: 150,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }) => money(value)
    },
    {
      headerName: 'ACCIONES', field: 'actions', minWidth: 130,
      sortable: false, filter: false,
      cellRenderer: ({ data }) => data?.isTotal ? null : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <BtnAcciones icon={<BorderColorIcon />} label="Editar" onClick={() => abrirModal(data)} />
          <BtnAcciones icon={<DeleteIcon />} label="Eliminar" onClick={() => eliminar(data.id)} />
        </Box>
      )
    }
  ], [eliminar]);

  const totalsRow = useMemo(() => ({
    id: 'totals',
    isTotal: true,
    asset: 'TOTAL',
    gallons: rows.reduce((sum, row) => sum + Number(row.gallons || 0), 0),
    amount: rows.reduce((sum, row) => sum + Number(row.amount || 0), 0)
  }), [rows]);

  const guardarFila = (saved) => {
    setRows((current) => {
      const exists = current.some((row) => row.id === saved.id);
      return exists
        ? current.map((row) => row.id === saved.id ? saved : row)
        : [saved, ...current];
    });
  };

  const exportarExcel = () => {
    const data = rows.map((row) => ({
      FECHA: formatDate(row.date),
      'TIPO DE UNIDAD': row.asset_type,
      UNIDAD: row.asset,
      OPERADOR: row.driver,
      'CANTIDAD GLLS': Number(row.gallons),
      IMPORTE: Number(row.amount)
    }));
    const sheet = XLSX.utils.json_to_sheet(data);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Fuel America');
    XLSX.writeFile(book, 'cargas_fuel_america.xlsx');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" fontWeight={700}>Cargas Fuel America</Typography>
        <Button variant="contained" color="inherit" startIcon={<AddCircleIcon />} onClick={() => abrirModal()}>
          AGREGAR
        </Button>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth label="Unidad" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex' }}>
            <DatePicker label="Fecha Inicial" value={startDate} onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true } }} />
            <IconButton onClick={() => setStartDate(null)}><ClearIcon /></IconButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex' }}>
            <DatePicker label="Fecha Final" value={endDate} onChange={setEndDate}
              minDate={startDate || undefined} slotProps={{ textField: { fullWidth: true } }} />
            <IconButton onClick={() => setEndDate(null)}><ClearIcon /></IconButton>
          </Grid>
          <Grid size={{ xs: 12, sm: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel>Filtro</InputLabel>
              <Select value={type} label="Filtro" onChange={(e) => setType(e.target.value)}>
                <MenuItem value="TODO">TODO</MenuItem>
                <MenuItem value="TRACTOR">TRACTOR</MenuItem>
                <MenuItem value="CAJA">CAJA</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 1.5 }}>
            <Button fullWidth variant="contained" startIcon={<ManageSearchIcon />} onClick={buscar}>
              BUSCAR
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button variant="contained" onClick={exportarExcel}>EXPORTAR A EXCEL</Button>
          </Grid>
        </Grid>
      </LocalizationProvider>

      <Box sx={{ height: 'calc(100vh - 330px)', minHeight: 420, mt: 1 }}>
        <AgGridReact
          ref={gridRef}
          modules={[AllCommunityModule]}
          rowData={rows}
          columnDefs={columns}
          pinnedBottomRowData={[totalsRow]}
          loading={loading}
          pagination
          paginationPageSize={100}
          defaultColDef={{ sortable: true, filter: true, resizable: true, flex: 1 }}
          getRowId={({ data }) => String(data.id)}
        />
      </Box>

      <CrearEditarFuelAmerica
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        carga={selected}
        onSaved={guardarFila}
      />
    </Box>
  );
};

export default CargasFuelAmerica;